import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const NAV_IDS = ["purchasing", "services", "method", "about", "cases", "careers", "contact"];

export default function Header() {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const navLabel = (id) => t.nav[id] || id;
    const testIdFor = (id, prefix = "nav-") =>
        `${prefix}${id === "cases" ? "benefits" : id}`;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-md border-b border-border"
                    : "bg-white border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between gap-3 lg:gap-4">
                {/* Logo - displayed in full (no crop) */}
                <a
                    href="#top"
                    data-testid="header-logo"
                    onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex items-center group flex-shrink-0"
                    aria-label="SITMEC home"
                >
                    <img
                        src="/sitmec-logo.jpg"
                        alt="SITMEC — Satisfaire vos exigences"
                        className="h-14 md:h-16 w-auto object-contain"
                    />
                </a>

                {/* Desktop nav */}
                <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
                    {NAV_IDS.map((id) => (
                        <button
                            key={id}
                            data-testid={testIdFor(id)}
                            onClick={() => go(id)}
                            className="font-display text-sm uppercase tracking-widest text-ink hover:text-mint-hover transition-colors whitespace-nowrap"
                        >
                            {navLabel(id)}
                        </button>
                    ))}
                </nav>

                {/* Right: language + CTA */}
                <div className="flex items-center gap-3 flex-shrink-0">
                    <div
                        className="hidden sm:flex items-center gap-1 font-display text-xs uppercase tracking-widest"
                        data-testid="lang-switcher"
                    >
                        <button
                            data-testid="lang-switcher-fr"
                            onClick={() => setLang("fr")}
                            className={`px-2 py-1 transition-colors ${
                                lang === "fr"
                                    ? "bg-ink text-white"
                                    : "text-ink hover:text-mint-hover"
                            }`}
                        >
                            FR
                        </button>
                        <span className="text-ink-muted">/</span>
                        <button
                            data-testid="lang-switcher-en"
                            onClick={() => setLang("en")}
                            className={`px-2 py-1 transition-colors ${
                                lang === "en"
                                    ? "bg-ink text-white"
                                    : "text-ink hover:text-mint-hover"
                            }`}
                        >
                            EN
                        </button>
                    </div>

                    <button
                        data-testid="header-cta"
                        onClick={() => go("contact")}
                        className="hidden md:inline-flex btn-primary text-xs px-4 py-3 whitespace-nowrap"
                    >
                        {t.nav.cta}
                    </button>

                    <button
                        data-testid="mobile-menu-toggle"
                        className="xl:hidden p-2 border border-ink"
                        onClick={() => setOpen(!open)}
                        aria-label="menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="xl:hidden bg-white border-t border-border px-6 py-6 space-y-4"
                >
                    {NAV_IDS.map((id) => (
                        <button
                            key={id}
                            data-testid={testIdFor(id, "mobile-nav-")}
                            onClick={() => go(id)}
                            className="block w-full text-left font-display text-base uppercase tracking-widest text-ink"
                        >
                            {navLabel(id)}
                        </button>
                    ))}
                    <div className="flex items-center gap-2 pt-2 font-display text-xs uppercase tracking-widest">
                        <button
                            data-testid="mobile-lang-fr"
                            onClick={() => setLang("fr")}
                            className={`px-3 py-1 border ${
                                lang === "fr"
                                    ? "bg-ink text-white border-ink"
                                    : "border-border"
                            }`}
                        >
                            FR
                        </button>
                        <button
                            data-testid="mobile-lang-en"
                            onClick={() => setLang("en")}
                            className={`px-3 py-1 border ${
                                lang === "en"
                                    ? "bg-ink text-white border-ink"
                                    : "border-border"
                            }`}
                        >
                            EN
                        </button>
                    </div>
                    <button
                        data-testid="mobile-cta"
                        onClick={() => go("contact")}
                        className="btn-primary w-full justify-center"
                    >
                        {t.nav.cta}
                    </button>
                </div>
            )}
        </header>
    );
}
